import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  type LucideIcon,
} from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { BlockInfo, StyleProperty } from "./documentEditor";
import { IMAGE_ALIGN_OPTIONS, TEXT_ALIGN_OPTIONS } from "./styleOptions";

/** The icon standing for each authored text alignment. */
const ALIGN_ICONS: Record<string, LucideIcon> = {
  left: AlignLeft,
  center: AlignCenter,
  right: AlignRight,
  justify: AlignJustify,
};

interface BlockStyleControlsProps {
  /** The block the knobs write to, or null when they have no target. */
  block: BlockInfo | null;
  /** Blocks editing (locked document, or an upload is in flight). */
  disabled: boolean;
  /** Writes one inline-style property on the block. */
  onStyleChange: (property: StyleProperty, value: string) => void;
}

/**
 * The toolbar's block-level alignment group for the selected block, writing
 * through the editor's style knobs. Size and colour live with the character
 * formatting group, which targets the block when no text session is open. An
 * open text session's block is still the selection, so the first click works
 * (mousedown keeps the editor focused; the toolbar commits the session before
 * writing the style). The control stays mounted and empty when there is no
 * target, so the bar never reflows; a selected fp-image aligns the picture
 * instead of text, and a repeater block disables it,
 * because its stamped output never renders the source tag's authored style.
 * @param block - The block the knobs write to, or null
 * @param disabled - Blocks editing
 * @param onStyleChange - Writes one inline-style property
 * @returns The block style controls
 */
export function BlockStyleControls({
  block,
  disabled,
  onStyleChange,
}: BlockStyleControlsProps) {
  const textAlign = block?.styles.textAlign ?? "";
  const knobsDisabled = disabled || block === null || block.repeater !== null;
  const isImage = block?.tagName === "fp-image";

  return (
    <div className="flex shrink-0 items-center gap-1">
      <ToggleGroup
        type="single"
        size="sm"
        className="gap-0.5"
        aria-label={isImage ? "Image align" : "Text align"}
        value={textAlign}
        disabled={knobsDisabled}
        onValueChange={(value) => onStyleChange("textAlign", value)}
      >
        {(isImage ? IMAGE_ALIGN_OPTIONS : TEXT_ALIGN_OPTIONS).map((align) => {
          const Icon = ALIGN_ICONS[align];
          return (
            <ToggleGroupItem
              key={align}
              value={align}
              title={`Align ${align}`}
              aria-label={`Align ${align}`}
              onMouseDown={(event) => event.preventDefault()}
            >
              <Icon />
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </div>
  );
}
